import { useEffect, useState } from "react";
import { CompanyDepartmentsTableProps } from ".";
import { Company } from "../../types/Company";
import { getCompanyByIdAsync, getCompanyDepartmentSummariesByCompanyIdAsync } from "../../apiClient";
import { showDangerDialog, showSuccessAlert } from "../../common";
import { CompanyDepartmentSummary } from "../../types/CompanyDepartmentSummary";

function useCompanyDepartmentsTable(props: CompanyDepartmentsTableProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [company, setCompany] = useState<Company>();
    const [companyDepartments, setCompanyDepartments] = useState<CompanyDepartmentSummary[]>();
    const [updateCompanyDepartmentId, setUpdateCompanyDepartmentId] = useState<number>();
    const [deleteCompanyDepartmentId, setDeleteCompanyDepartmentId] = useState<number>();
    const [showCreateCompanyDepartmentModal, setShowCreateCompanyDepartmentModal] = useState(false);

    const showUpdateCompanyDepartmentModal = !!updateCompanyDepartmentId;
    const showDeleteCompanyDepartmentModal = !!deleteCompanyDepartmentId;

    async function fetchCompanyDepartmentsAsync() {
        const result = await getCompanyDepartmentSummariesByCompanyIdAsync(props.companyId);

        if (result.failed) {
            console.error("Failed to fetch company departments for company with id", props.companyId, "Error:", result.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("FetchCompanyDepartmentsError"));
            return;
        }

        setCompanyDepartments(result.value);
    }

    useEffect(() => {
        async function fetchDataAsync() {
            const [companyResult, companyDepartmentsResult] = await Promise.all([
                getCompanyByIdAsync(props.companyId),
                getCompanyDepartmentSummariesByCompanyIdAsync(props.companyId)
            ]);

            if (companyResult.failed) {
                console.error("Failed to fetch company with id", props.companyId, "Error:", companyResult.error);
                showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("FetchCompanyError"));
                return;
            }

            if (companyDepartmentsResult.failed) {
                console.error("Failed to fetch company departments for company with id", props.companyId, "Error:", companyDepartmentsResult.error);
                showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("FetchCompanyDepartmentsError"));
                return;
            }

            setCompany(companyResult.value);
            setCompanyDepartments(companyDepartmentsResult.value);
            setIsLoading(false);
        }

        fetchDataAsync();

    }, [props.companyId]);

    async function handleCompanyDepartmentCreated() {
        setShowCreateCompanyDepartmentModal(false);
        showSuccessAlert(props.resources.get("CompanyDepartmentCreated"));
        await fetchCompanyDepartmentsAsync();
    }

    async function handleCompanyDepartmentUpdated() {
        setUpdateCompanyDepartmentId(undefined);
        showSuccessAlert(props.resources.get("CompanyDepartmentUpdated"));
        await fetchCompanyDepartmentsAsync();
    }

    async function handleCompanyDepartmentDeleted() {
        showSuccessAlert(props.resources.get("CompanyDepartmentDeleted"));
        await fetchCompanyDepartmentsAsync();
    }

    return {
        isLoading,
        company,
        companyDepartments,
        showCreateCompanyDepartmentModal,
        showUpdateCompanyDepartmentModal,
        showDeleteCompanyDepartmentModal,
        updateCompanyDepartmentId,
        deleteCompanyDepartmentId,
        handleCompanyDepartmentCreated,
        handleCompanyDepartmentUpdated,
        handleCompanyDepartmentDeleted,
        handleShowCreateCompanyDepartmentModal: () => setShowCreateCompanyDepartmentModal(true),
        handleCloseCreateCompanyDepartmentModal: () => setShowCreateCompanyDepartmentModal(false),
        handleShowUpdateCompanyDepartmentModal: (id: number) => setUpdateCompanyDepartmentId(id),
        handleCloseUpdateCompanyDepartmentModal: () => setUpdateCompanyDepartmentId(undefined),
        handleShowDeleteCompanyDepartmentModal: (id: number) => setDeleteCompanyDepartmentId(id),
        handleCloseDeleteCompanyDepartmentModal: () => setDeleteCompanyDepartmentId(undefined),
    };

}

export default useCompanyDepartmentsTable;
